import { motion } from "framer-motion";
import { useT } from "@/i18n/LanguageContext";

const FLAGS = [
    { flag: "🇫🇷", country: "France" },
    { flag: "🇱🇻", country: "Latvia" },
    { flag: "🇱🇹", country: "Lithuania" },
    { flag: "🇨🇭", country: "Switzerland" },
    { flag: "🇦🇲", country: "Armenia" },
    { flag: "🇵🇹", country: "Portugal" },
    { flag: "🇪🇪", country: "Estonia" },
    { flag: "🇭🇰", country: "Hong Kong" },
];

const fadeUp = {
    hidden: { opacity: 0, y: 20 },
    visible: (i = 0) => ({
        opacity: 1,
        y: 0,
        transition: { duration: 0.6, ease: "easeOut", delay: i * 0.08 },
    }),
};

export default function Hero() {
    const { t } = useT();
    const tags = t.hero.tags;

    return (
        <section
            id="about"
            data-testid="hero-section"
            className="relative min-h-[92vh] flex items-center pt-32 pb-24 overflow-hidden"
        >
            <div
                aria-hidden="true"
                className="absolute inset-0 opacity-[0.12] mix-blend-screen pointer-events-none"
                style={{
                    backgroundImage:
                        "url('https://static.prod-images.emergentagent.com/jobs/a62b47d9-28ab-47fe-91c2-f4f8edce9f70/images/edc8dc2dc48aae922c02d7e26fe0ad11c2cf392ccef255a74baf4e578b0009bb.png')",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                }}
            />
            <div
                aria-hidden="true"
                className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full pointer-events-none"
                style={{
                    background:
                        "radial-gradient(closest-side, rgba(201,169,110,0.10), rgba(201,169,110,0) 70%)",
                }}
            />

            <div className="relative max-w-[1280px] mx-auto px-6 md:px-12 w-full">
                <motion.p
                    initial="hidden"
                    animate="visible"
                    custom={0}
                    variants={fadeUp}
                    className="text-[11px] uppercase tracking-[0.14em] text-[#C9A96E] font-medium mb-8"
                    data-testid="hero-label"
                >
                    {t.hero.label}
                </motion.p>

                <motion.h1
                    initial="hidden"
                    animate="visible"
                    custom={1}
                    variants={fadeUp}
                    className="font-display text-[42px] font-medium leading-[1.15] tracking-[-0.02em] max-w-[1000px]"
                    data-testid="hero-headline"
                >
                    {t.hero.h1Line1}
                    <br />
                    <span className="text-[#C9A96E]">{t.hero.h1Line2}</span>
                    <br />
                    {t.hero.h1Line3}
                </motion.h1>

                <motion.p
                    initial="hidden"
                    animate="visible"
                    custom={2}
                    variants={fadeUp}
                    className="mt-8 max-w-[640px] text-[15px] leading-[1.7] text-[rgba(232,230,223,0.7)]"
                    data-testid="hero-subtitle"
                >
                    {t.hero.subtitle}
                </motion.p>

                <motion.div
                    initial="hidden"
                    animate="visible"
                    custom={3}
                    variants={fadeUp}
                    className="mt-10 flex flex-wrap items-center gap-4"
                >
                    <a
                        href="#contact"
                        data-testid="hero-cta-primary"
                        className="inline-flex items-center justify-center px-[24px] py-[12px] bg-[#C9A96E] text-[#0D0D1A] rounded-[4px] text-[14px] font-medium hover:bg-[#d4b986] hover:-translate-y-0.5 transition-all"
                    >
                        {t.hero.ctaPrimary}
                    </a>
                    <a
                        href="#thesis"
                        data-testid="hero-cta-secondary"
                        className="inline-flex items-center justify-center gap-2 px-[24px] py-[12px] border-[0.5px] border-[rgba(232,230,223,0.2)] text-[#E8E6DF] rounded-[4px] text-[14px] font-medium hover:bg-[rgba(232,230,223,0.05)] hover:border-[rgba(232,230,223,0.4)] transition-all"
                    >
                        {t.hero.ctaSecondary}
                        <span aria-hidden="true">↓</span>
                    </a>
                </motion.div>

                <motion.div
                    initial="hidden"
                    animate="visible"
                    custom={4}
                    variants={fadeUp}
                    className="mt-14 flex flex-wrap items-center gap-x-3 gap-y-2 text-[13px] text-[rgba(232,230,223,0.5)]"
                    data-testid="hero-tags"
                >
                    {tags.map((tag, i) => (
                        <span key={tag} className="flex items-center gap-3">
                            {tag}
                            {i < tags.length - 1 && (
                                <span className="text-[#C9A96E]/40">·</span>
                            )}
                        </span>
                    ))}
                </motion.div>

                <motion.div
                    initial="hidden"
                    animate="visible"
                    custom={5}
                    variants={fadeUp}
                    className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-3"
                    data-testid="hero-flags"
                >
                    <div className="flex flex-wrap items-center gap-2 text-[20px]">
                        {FLAGS.map((f) => (
                            <span
                                key={f.country}
                                title={f.country}
                                aria-label={f.country}
                                className="hover:scale-110 transition-transform"
                            >
                                {f.flag}
                            </span>
                        ))}
                    </div>
                    <span className="text-[12px] text-[rgba(232,230,223,0.45)] italic">
                        {t.hero.comingNote}
                    </span>
                </motion.div>
            </div>
        </section>
    );
}
